import { useCallback, useRef, useState } from 'react';
import Loading from './Loading';
import { TrashIcon } from '@heroicons/react/24/outline';
import axios, { type AxiosProgressEvent, type CancelToken } from 'axios';
import ViewUpload from '~/components/ViewUpload';
import { S3File } from '~/lib/Api.types';

/**
 * Copied from Vapor.store!
 * axios wasn't importing correctly there
 * possibly fixed with: https://github.com/laravel/vapor-js/pull/58
 * for now this is small, just keep it here for more control
 *
 * @param file
 * @param options
 * @returns {Promise<any>}
 */
async function store(
    file?: File,
    options: {
        progress?: (progress: number) => void;
        signedStorageUrl?: string;
        bucket?: string;
        contentType?: string;
        expires?: string;
        visibility?: string;
        data?: Record<string, unknown>;
        baseURL?: string;
        headers?: Record<string, string>;
        options?: Record<string, any>;
        cancelToken?: CancelToken;
    } = {},
) {
    const response = await axios.post(
        options.signedStorageUrl
            ? options.signedStorageUrl
            : '/vapor/signed-storage-url',
        {
            bucket: options.bucket || '',
            content_type: options.contentType || file?.type,
            expires: options.expires || '',
            visibility: options.visibility || '',
            ...options.data,
        },
        {
            baseURL: options.baseURL,
            headers: options.headers || {},
            ...options.options,
        },
    );

    const headers = response.data.headers;

    if ('Host' in headers) {
        delete headers.Host;
    }

    if (typeof options.progress === 'undefined') {
        options.progress = () => {};
    }

    const cancelToken = options.cancelToken;

    await axios.put(response.data.url, file, {
        cancelToken,
        headers: headers,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            options?.progress?.(progressEvent.loaded / progressEvent.total!);
        },
    });

    response.data.extension = (file?.name ?? '').split('.').pop();

    return response.data;
}

interface FileUploadProps {
    value?: {
        uuid?: string;
        key?: string;
        bucket?: string;
        name?: string;
        content_type?: string;
        delete?: boolean;
        upload?: any;
        file?: File;
    } | null;
    onChange: (file: S3File | File | { delete: boolean }) => void;
    className?: string;
    buttonClassName?: string;
    id?: string;
    accept?: string;
    showName?: boolean;
    disabled?: boolean;
    skipUploadingFile?: boolean;
    children?: React.ReactNode;
}

export async function uploadToS3(
    firstFile: File,
    onChange: FileUploadProps['onChange'] = () => {},
    setProgress: (progress: number) => void = () => {},
    skipUploadingFile: boolean = false,
) {
    if (skipUploadingFile) {
        onChange(firstFile);
        return;
    }
    const response: S3File = /*Vapor.*/ await store(firstFile, {
        progress: setProgress,
    });
    setProgress(-1);
    onChange({
        uuid: response.uuid,
        key: response.key,
        bucket: response.bucket,
        name: firstFile.name,
        content_type: firstFile.type,
    });
    return response;
}

const FileUpload = ({
    value,
    onChange,
    className,
    id,
    accept,
    showName,
    disabled,
    skipUploadingFile,
    buttonClassName = '',
    children,
}: FileUploadProps) => {
    const textInputRef = useRef<HTMLInputElement>(null);
    const [progress, setProgress] = useState<number>(-1);
    const [error, setError] = useState<string>('');

    const onFileSelected = useCallback(() => {
        const el = textInputRef.current;

        if (!el?.files?.[0]) {
            return;
        }

        const file = el.files[0];
        const maxSizeInBytes = 10 * 1024 * 1024; // 10MB in bytes

        // Clear any previous error
        setError('');

        // Check file size
        if (file.size > maxSizeInBytes) {
            setError('File size must be less than 10MB');
            // Clear the input
            el.value = '';
            return;
        }

        uploadToS3(file, onChange, setProgress, skipUploadingFile);
    }, [onChange, setProgress, skipUploadingFile]);

    const previewImage = textInputRef?.current?.files?.[0]
        ? URL.createObjectURL(textInputRef.current?.files[0])
        : null;

    return (
        <div className={`flex items-center ${className}`}>
            <label>
                {(accept === 'image/*' ||
                    value?.content_type?.startsWith('image/')) &&
                    (previewImage || value) &&
                    !value?.delete && (
                        <>
                            {!!previewImage && (
                                <img
                                    src={previewImage}
                                    className="w-[100px] border mb-2"
                                />
                            )}
                            {!previewImage && !!value && (
                                <ViewUpload
                                    file={value?.upload || value}
                                    isImage={true}
                                    className="w-[100px] border mb-2"
                                />
                            )}
                        </>
                    )}
                {!!showName && value?.name}
                <input
                    type="file"
                    id={id}
                    className="hidden"
                    ref={textInputRef}
                    onChange={onFileSelected}
                    accept={accept}
                    disabled={disabled}
                />
                <div
                    className={`btn-light-pill inline-block cursor-pointer ${buttonClassName}`}
                >
                    Upload
                </div>
                {progress > -1 && <Loading />}
            </label>
            {error && (
                <div className="ml-2 text-red-600 text-sm">
                    {error}
                </div>
            )}
            {(previewImage || value) && !value?.delete && (
                <button
                    type="button"
                    className="ml-4 bg-red-100 hover:bg-red-200 text-red-600 rounded-full flex items-center justify-center w-10 h-10"
                    onClick={() => onChange({ delete: true })}
                >
                    <TrashIcon className="w-6 h-6" />
                </button>
            )}
            {children}
        </div>
    );
};

export default FileUpload;
